import React from "react";
import Select from "react-select";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import ImageUpload from "../../components/CustomComponents/ImageUpload/imageUpload";
import CustomDateTimePicker from "../../components/DateTimePicker/dateTimePicker";
import { DealSection, DealToggleRow } from "./dealFormUI";
import "./dealForm.css";

const PRICING_OPTIONS = [
  { value: "CustomPrice", label: "Fixed Price" },
  { value: "Discount", label: "Custom Discount %" },
  { value: "SlotPricing", label: "Slot Pricing" },
];

const PRICING_LABELS = {
  Fixed: "Used Deal Pricing",
  Free: "Free",
  HalfPrice: "Half Price (50% Off)",
  Discount: "Custom Discount %",
  CustomPrice: "Fixed Price",
  SlotPricing: "Slot Pricing",
};

/**
 * Shared Basics sections for Create Deal and Update Deal.
 */
function DealFormBasics({
  themeColor,
  formState,
  toggles,
  imageState,
  setImageState,
  errors = {},
  handleInputChange,
  handlePricingTypeChange,
  handleToggleChange,
  dropdownStyles,
  getCurrencySymbol,
  showMinimumOrder = false,
}) {
  const pricingType = formState.dealPricingType || "CustomPrice";
  const pricingValue = {
    value: pricingType,
    label: PRICING_LABELS[pricingType] || pricingType || "Fixed Price",
  };

  return (
    <div className="deal-form-shell" style={{ "--df-accent": themeColor }}>
      <DealSection icon="fa-eye" label="Visibility">
        <div className="deal-basics-media">
          <div className="deal-basics-media__image">
            <ImageUpload
              imageUrl={imageState.dealImage}
              imageFile={imageState.imageFile}
              setImage={(url, file) =>
                setImageState({ dealImage: url, imageFile: file })
              }
            />
          </div>
          <div className="deal-options-grid">
            <DealToggleRow
              isOn={!!toggles.isActive}
              title="Active"
              hint="Deal is available when enabled"
            >
              <ToggleSwitch
                label=""
                isChecked={toggles.isActive}
                onToggle={(checked) => handleToggleChange("isActive", checked)}
                themeColor={themeColor}
              />
            </DealToggleRow>
            <DealToggleRow
              isOn={!!toggles.showInKiosk}
              title="Show in Kiosk"
              hint="Visible on self-service kiosks"
            >
              <ToggleSwitch
                label=""
                isChecked={toggles.showInKiosk}
                onToggle={(checked) => handleToggleChange("showInKiosk", checked)}
                themeColor={themeColor}
              />
            </DealToggleRow>
            <DealToggleRow
              isOn={!!toggles.showInPOS}
              title="Show in POS"
              hint="Visible on POS terminals"
            >
              <ToggleSwitch
                label=""
                isChecked={toggles.showInPOS}
                onToggle={(checked) => handleToggleChange("showInPOS", checked)}
                themeColor={themeColor}
              />
            </DealToggleRow>
            <DealToggleRow
              isOn={!!toggles.showInOnline}
              title="Show in Online"
              hint="Visible on online ordering"
            >
              <ToggleSwitch
                label=""
                isChecked={toggles.showInOnline}
                onToggle={(checked) => handleToggleChange("showInOnline", checked)}
                themeColor={themeColor}
              />
            </DealToggleRow>
          </div>
        </div>
      </DealSection>

      <DealSection icon="fa-info-circle" label="Deal details">
        <div className="deal-basics-card">
          <div className="deal-form-fields-row">
            <div className="deal-form-field">
              <FormInput
                label="Deal Name *"
                type="text"
                value={formState.dealName}
                onchange={(e) => handleInputChange("dealName", e.target.value)}
                error={errors.dealName}
                placeHolder="Enter deal name"
              />
            </div>
            <div className="deal-form-field">
              <label className="form-label">Deal Pricing Type</label>
              <Select
                options={PRICING_OPTIONS}
                value={pricingValue}
                onChange={(option) => {
                  const newPricingType = option ? option.value : "CustomPrice";
                  // Batch related pricing fields so creates/updates stay in sync
                  if (typeof handlePricingTypeChange === "function") {
                    handlePricingTypeChange(newPricingType);
                  } else {
                    handleInputChange("dealPricingType", newPricingType);
                    if (newPricingType !== "CustomPrice") {
                      handleInputChange("dealPrice", "");
                    }
                    if (newPricingType !== "Discount") {
                      handleInputChange("dealDiscountPercentage", "");
                    }
                  }
                }}
                styles={dropdownStyles}
                placeholder="Fixed Price"
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                menuShouldBlockScroll={false}
                menuShouldScrollIntoView={false}
                closeMenuOnScroll={false}
              />
            </div>
          </div>

          {pricingType === "CustomPrice" && (
            <div className="deal-form-fields-row">
              <div className="deal-form-field">
                <FormInput
                  label={`Deal Price (${getCurrencySymbol()}) *`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={formState.dealPrice}
                  onchange={(e) => handleInputChange("dealPrice", e.target.value)}
                  error={errors.dealPrice}
                  placeHolder="Enter fixed deal price"
                />
              </div>
            </div>
          )}

          {pricingType === "Discount" && (
            <div className="deal-form-fields-row">
              <div className="deal-form-field">
                <FormInput
                  label="Discount Percentage"
                  type="number"
                  min="0"
                  max="100"
                  value={formState.dealDiscountPercentage}
                  onchange={(e) =>
                    handleInputChange("dealDiscountPercentage", e.target.value)
                  }
                  placeHolder="0-100"
                />
              </div>
            </div>
          )}

          <div className="deal-form-fields-row mb-0">
            <div className="deal-form-field deal-form-field--full">
              <label className="form-label">Deal Description</label>
              <textarea
                className="deal-textarea"
                rows="4"
                value={formState.dealDescription}
                onChange={(e) => handleInputChange("dealDescription", e.target.value)}
                placeholder="Enter deal description"
              />
            </div>
          </div>

          {showMinimumOrder && (
            <div className="deal-form-fields-row mt-3 mb-0">
              <div className="deal-form-field">
                <FormInput
                  label={`Minimum Order Value (${getCurrencySymbol()})`}
                  type="number"
                  step="0.01"
                  min="0"
                  value={formState.minimumOrderValue}
                  onchange={(e) =>
                    handleInputChange("minimumOrderValue", e.target.value)
                  }
                  placeHolder="0.00"
                />
                <p className="deal-field-hint">
                  Leave empty for no minimum order requirement
                </p>
              </div>
            </div>
          )}
        </div>
      </DealSection>

      <DealSection icon="fa-calendar" label="Availability dates">
        <div className="deal-form-fields-row mb-0">
          <div className="deal-form-field">
            <label className="form-label">Start Date</label>
            <div className="deal-date-wrap">
              <CustomDateTimePicker
                value={formState.startDate}
                onChange={(date) => handleInputChange("startDate", date)}
                placeholder="Select start date"
              />
            </div>
          </div>
          <div className="deal-form-field">
            <label className="form-label">End Date</label>
            <div className="deal-date-wrap">
              <CustomDateTimePicker
                value={formState.endDate}
                onChange={(date) => handleInputChange("endDate", date)}
                placeholder="Select end date"
              />
            </div>
          </div>
        </div>
      </DealSection>
    </div>
  );
}

export default DealFormBasics;
